import BurstModeOutlinedIcon from '@mui/icons-material/BurstModeOutlined';
import { Card, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { FC, ReactNode } from 'react';
import type { IIsPdf } from '../../models';
import { AvaniLightTheme } from '../../theme/schemes/AvaniLightTheme';

interface AvaniCardProps extends IIsPdf {
  children: ReactNode;
  title?: string;
  titleIcon?: ReactNode;
  icon?: ReactNode;
  temp?: boolean;
  white?: boolean;
  grey?: boolean;
  short?: boolean;
  green?: boolean;
  isHighTemp?: boolean;
  small?: boolean;
  padded?: boolean;
  sx?: any;
  onClick?: () => void;
  customTitle?: boolean;
  toggleImages?: boolean;
  headerFlexDirection?: 'column' | 'row';
}

const AvaniStyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'isPDF' && prop !== 'customTitle'
})<{ isPDF: boolean; customTitle?: boolean }>(({ theme, isPDF, customTitle }) => {
  const currentTheme = isPDF ? AvaniLightTheme : theme;

  return `
  border-radius: 16px;
  padding: 16px 0;

  &.AvaniCardSmall {
    padding: 16px 0 8px 0;
  }

  &.AvaniCardPadded {
    padding: 16px;
  }

  .MuiTypography-root.MuiTypography-h3 {
    margin: 0 0 0 16px;
    line-height: ${customTitle ? 2 : 1};
    padding-bottom: ${customTitle ? '0px' : '24px'};
  }

  &.AvaniCardShort .MuiTypography-root.MuiTypography-h3 {
    padding-bottom: 0;
  }
  
  &.AvaniCardPadded .MuiTypography-root.MuiTypography-h3 {
    margin: 0;
    padding-bottom: ${customTitle ? '0px' : '12px'};
  }

  .MuiTypography-root.MuiTypography-h3.AvaniCardTitleTemp {
    flex-grow: 1;
  }

  .MuiTypography-root.MuiTypography-h3.AvaniSmallCardTitle {
    padding-bottom: 20px;
  }

  .MuiTypography-root.MuiTypography-h3.AvaniDarkCardTitle {
    color: ${currentTheme.colors.avani.teal};
    padding-bottom: 0px;
  }
`;
});

export const AvaniCard: FC<AvaniCardProps> = ({
  children,
  title,
  titleIcon,
  icon,
  temp,
  isHighTemp,
  white,
  small,
  padded,
  grey,
  green,
  short,
  sx,
  onClick,
  customTitle = false,
  isPDF = false,
  toggleImages = false,
  headerFlexDirection = 'row'
}) => {
  const theme = isPDF ? AvaniLightTheme : useTheme();

  return (
    <AvaniStyledCard
      isPDF={isPDF}
      raised={true}
      sx={{
        height: '100%',
        backgroundColor: isHighTemp
          ? theme.colors.avani.lightMagenta
          : grey
            ? theme.colors.avani.aGrey
            : green
              ? theme.colors.avani.turc
              : white
                ? theme.colors.alpha.white[100]
                : theme.colors.avani.lightTurc,
        ['.MuiPieArc-root']: { stroke: 'none' },
        ...sx
      }}
      customTitle={customTitle}
      onClick={onClick ? () => onClick() : undefined}
      className={small ? 'AvaniCardSmall' : padded ? 'AvaniCardPadded' : short ? 'AvaniCardShort' : ''}
    >
      <div style={{ display: title ? 'flex' : 'none', flexDirection: headerFlexDirection }}>
        {title && (
          <>
            <Typography
              variant="h3"
              color={green ? 'white' : theme.colors.avani.teal}
              className={temp ? 'AvaniCardTitleTemp' : small ? 'AvaniSmallCardTitle' : grey ? 'AvaniDarkCardTitle' : ''}
              sx={{ flexGrow: 1, width: 'calc(100% - 56px)', display: 'flex', alignItems: 'center' }}
            >
              {titleIcon && titleIcon}
              {title}
            </Typography>
          </>
        )}
        {icon && <span style={{ paddingRight: padded ? 0 : '16px' }}>{icon}</span>}
        {toggleImages && (
          <BurstModeOutlinedIcon
            fontSize={'large'}
            sx={{ marginRight: '0.5rem', marginTop: '-0.5rem', color: theme.colors.avani.teal }}
          />
        )}
      </div>
      {children}
    </AvaniStyledCard>
  );
};
